import _ from 'lodash';
import { produce } from 'immer';
import { appLog } from 'utils/logger';
import { componentWalk } from 'utils/blocks/component-walk';
import { DEFAULT_MEDIA_QUERY_PLATFORMS, COMPONENT_STATES_ORDERED, LAYER_TYPE, CARD_TYPE } from 'common/constants';
import type {
	BBModel,
	StoryDefaultPlatform,
	StoryMediaPlatform,
	StoryStep,
	CardData,
	WithStateAndPlatform,
} from 'types/story';

// Check if card has specific features by type
function hasCardFeature(type: CardData['type']) {
	return {
		results: type === CARD_TYPE.POLL || type === CARD_TYPE.THIS_OR_THAT,
		correct: type === CARD_TYPE.TRIVIA || type === CARD_TYPE.TRUE_OR_FALSE || type === CARD_TYPE.SORTABLE_TRIVIA,
		score: type === CARD_TYPE.TRIVIA || type === CARD_TYPE.TRUE_OR_FALSE,
		answer:
			type === CARD_TYPE.TRIVIA ||
			type === CARD_TYPE.SORTABLE_TRIVIA ||
			type === CARD_TYPE.SORTABLE_POLL ||
			type === CARD_TYPE.POLL ||
			type === CARD_TYPE.TRUE_OR_FALSE ||
			type === CARD_TYPE.THIS_OR_THAT ||
			type === CARD_TYPE.PERSONALITY_TEST,
		answerScore: type === CARD_TYPE.PERSONALITY_TEST || type === CARD_TYPE.SORTABLE_TRIVIA,
		answerAttempts: type === CARD_TYPE.TRIVIA,
		multipleAnswer: type === CARD_TYPE.POLL || type === CARD_TYPE.SORTABLE_TRIVIA,
		submitButton: type === CARD_TYPE.POLL || type === CARD_TYPE.SORTABLE_POLL || type === CARD_TYPE.FORM,
		autoSubmitOnComplete: type === CARD_TYPE.SORTABLE_TRIVIA,
	};
}

/**
 * Story mediaQuery.defaultPlatform is a real breakpoint (`desktop`, `mobile`, …).
 * `DEFAULT` is only a recipe placeholder. Writing onto it and then deleting
 * `default` would wipe the values.
 */
function asStoryDefaultPlatform(platform: StoryMediaPlatform): StoryDefaultPlatform {
	if (platform === DEFAULT_MEDIA_QUERY_PLATFORMS.DEFAULT) {
		throw new Error('Story default platform cannot be "default"');
	}

	return platform;
}

function mergePlatformValues<T extends object>(recipeDefault: T | undefined, storyValues: T | undefined): T {
	return { ...recipeDefault, ...storyValues } as T;
}

function transformStatePlatformMap<T>(
	values: WithStateAndPlatform<T> | undefined,
	storyPlatform: StoryDefaultPlatform,
	merge: (recipeDefault: T | undefined, storyValues: T | undefined) => T
) {
	if (!values) {
		return;
	}

	COMPONENT_STATES_ORDERED.forEach(state => {
		const byPlatform = values[state];
		if (!byPlatform) {
			return;
		}

		const recipeDefault = byPlatform[DEFAULT_MEDIA_QUERY_PLATFORMS.DEFAULT];
		const storyValues = byPlatform[storyPlatform];

		if (recipeDefault !== undefined || storyValues !== undefined) {
			byPlatform[storyPlatform] = merge(recipeDefault, storyValues);
		}

		delete byPlatform[DEFAULT_MEDIA_QUERY_PLATFORMS.DEFAULT];
	});
}

function transformComponent(component: BBModel, storyPlatform: StoryDefaultPlatform) {
	const componentProps = component.uiConfig?.componentProps;

	if (componentProps) {
		const { styles, other, animation } = componentProps;
		transformStatePlatformMap(styles, storyPlatform, mergePlatformValues);
		transformStatePlatformMap(other, storyPlatform, mergePlatformValues);
		transformStatePlatformMap(animation, storyPlatform, mergePlatformValues);
	}

	const { children } = component;
	if (Array.isArray(children)) {
		componentWalk(children, ({ component: child }) => transformComponent(child, storyPlatform));
	} else {
		// Text / button children: { state: { platform: string } }. Relocate, do not spread.
		transformStatePlatformMap(
			children,
			storyPlatform,
			(recipeDefault, storyValues) => (storyValues ?? recipeDefault) as string
		);
	}
}

/**
 * Transform element(s) and their children values which are configurable
 * by state or media query and have predefined recipe defaults.
 *
 * Picks "default" media-query values, writes them to the story
 * `defaultPlatform` key, then deletes the "default" key.
 *
 * Common use cases: add a new element, instantiate a card from a template
 * (`CardFacade.transformElementsDefaults`).
 *
 * Mutate draft fields directly — lodash get/set can bypass immer proxies.
 *
 * @param defaultPlatform Story mediaQuery.defaultPlatform (never `"default"`)
 * @param dataOrigin Card elements array or a single element
 */
function transformElementDefaults<T extends BBModel | BBModel[]>(
	defaultPlatform: StoryMediaPlatform,
	dataOrigin: T
): T {
	const storyPlatform = asStoryDefaultPlatform(defaultPlatform);
	const log = appLog.extend('utils:transformElementDefaults');
	log('RUN');

	if (Array.isArray(dataOrigin)) {
		return produce(dataOrigin, draft => {
			componentWalk(draft, ({ component }) => transformComponent(component, storyPlatform));
		}) as T;
	}

	return produce(dataOrigin, draft => {
		transformComponent(draft as BBModel, storyPlatform);
	});
}

/**
 * Change layer type to move element to Content or FLA|FLB building blocks
 *
 * @param elementOrigin {Object} Story card element object
 * @param discard {Boolean} transform element from float children to default
 * @return {Object} new edited elementOrigin
 */
function transformElementToFloatChildren(elementOrigin, discard = false) {
	return produce(elementOrigin, draft => {
		_.set(draft, 'uiConfig.layer', discard ? LAYER_TYPE.CONTENT : LAYER_TYPE.FLOAT);
	});
}

/**
 * @param steps {Array} Story steps
 * @param cardId {String} searchable card id
 * @return {String}
 */
function getPathToCardInSteps(steps: StoryStep[], cardId: string) {
	return _.reduce(
		steps,
		(memo, step, index) => {
			const cardIndex = _.findIndex(step.cards, card => card._id === cardId);
			if (cardIndex !== -1) {
				return `${index}.cards.${cardIndex}`;
			}
			return memo;
		},
		''
	);
}

export {
	transformElementDefaults,
	transformElementToFloatChildren,
	getPathToCardInSteps,
	hasCardFeature,
	asStoryDefaultPlatform,
};
